import { FormControlLabel } from '@mui/material';
import { useFormState } from 'react-final-form';

import { PreviewYaml } from 'components/code';
import {
  ControlWithDescription,
  InlineWithDescription,
} from 'components/form-layouts';
import { CustomLabelsStack } from 'features/cluster/autoscaler/hooks/useNodeTemplateDrawer/CustomLabelsStack';
import { CustomTaintsStack } from 'features/cluster/autoscaler/hooks/useNodeTemplateDrawer/CustomTaintsStack';

import { ProvisionNodesCloudConfigurationFormValues } from '../../types';

import { RffCheckbox } from '@cast/shared-rff-ui';

import { Typography, Box, Stack } from '@cast/design-system';

export const KubernetesOrchestrationSection = () => {
  const { values } = useFormState<ProvisionNodesCloudConfigurationFormValues>({
    subscription: { values: true },
  });

  const { shouldTaint, shouldUseCustomLabels, _customTaints, _customLabels } =
    values;

  const customTaints = _customTaints?.filter((t) => t?.key) ?? [];
  const customLabels = _customLabels?.filter((l) => l?.key) ?? [];

  const yamlContent = {
    ...(shouldUseCustomLabels
      ? {
          nodeSelector: customLabels.length
            ? Object.assign(
                {},
                ...customLabels.map(({ key, value }) => ({
                  [key]: `"${value ?? ''}"`,
                }))
              )
            : '',
        }
      : {}),
    ...(shouldTaint
      ? {
          tolerations: customTaints.map(({ key, value, effect }) => ({
            key: `"${key}"`,
            value: `"${value ?? ''}"`,
            effect: `"${effect}"`,
          })),
        }
      : {}),
  };
  return (
    <InlineWithDescription
      description={
        <ControlWithDescription label="Kubernetes orchestration">
          Control how workloads are scheduled onto this node.
        </ControlWithDescription>
      }
      controls={
        <Stack gap="24px">
          <Stack gap="14px">
            <FormControlLabel
              sx={{ gap: '8px', ml: 0 }}
              control={
                <RffCheckbox
                  sx={{ alignSelf: 'flex-start', marginTop: '2px' }}
                  size="small"
                  name="shouldTaint"
                  testId="should-taint-toggle"
                />
              }
              label={
                <Stack pt={2} gap={2}>
                  <Typography variant="P12M">Taint nodes</Typography>
                  <Typography variant="P12R" color="textSecondary">
                    Pods without matching tolerations will not be scheduled on
                    nodes provisioned in this edge location.
                  </Typography>
                </Stack>
              }
            />
            {shouldTaint && <CustomTaintsStack disabled={false} />}
          </Stack>

          <Stack gap="14px">
            <FormControlLabel
              sx={{ gap: '8px', ml: 0 }}
              control={
                <RffCheckbox
                  sx={{ alignSelf: 'flex-start', marginTop: '2px' }}
                  size="small"
                  name="shouldUseCustomLabels"
                  testId="use-custom-labels-toggle"
                />
              }
              label={
                <Stack pt={2} gap={2}>
                  <Typography variant="P12M">Use custom labels</Typography>
                  <Typography variant="P12R" color="textSecondary">
                    Apply custom labels to nodes provisioned in this edge
                    location.
                  </Typography>
                </Stack>
              }
            />
            {shouldUseCustomLabels && <CustomLabelsStack disabled={false} />}
          </Stack>

          {(shouldTaint || shouldUseCustomLabels) && (
            <Box position="relative" maxWidth={392}>
              <PreviewYaml
                data={yamlContent}
                size="md"
                containerSx={{ padding: '10px 12px' }}
              />
            </Box>
          )}
        </Stack>
      }
    />
  );
};
